/**
 * Plantillas de comunicación: persistencia demo en localStorage (sin env vars).
 * Las filas demo base vienen de `DEMO_TEMPLATES`; ediciones y custom se guardan acá.
 */

import { DEMO_TEMPLATES } from '../data/commsNotificationsDemo';

const LS_KEY = 'spectra-rrhh-comms-templates-v1';
const EVT = 'spectra-rrhh-templates-updated';

function safeParse(raw) {
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

/** @typedef {{ id: string, key: string, category: string, channel: string, status: string, updatedAt: string, vars: string, displayName?: string, previewText?: string, isCustom?: boolean }} CommsTemplateRow */

export function readTemplatesStore() {
  try {
    const o = safeParse(localStorage.getItem(LS_KEY) || '{}');
    const overrides = o.overrides && typeof o.overrides === 'object' ? o.overrides : {};
    const customs = Array.isArray(o.customs) ? o.customs : [];
    return { overrides, customs };
  } catch {
    return { overrides: {}, customs: [] };
  }
}

function writeTemplatesStore(data) {
  localStorage.setItem(LS_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(EVT));
}

/** @returns {CommsTemplateRow[]} */
export function getMergedTemplatesList() {
  const { overrides, customs } = readTemplatesStore();
  const base = DEMO_TEMPLATES.map((tpl) => {
    const ov = overrides[tpl.key] || {};
    return {
      ...tpl,
      ...ov,
      id: tpl.id,
      key: tpl.key,
      isCustom: false,
    };
  }).filter((row) => !row.hidden);
  const extra = customs.map((c) => ({
    ...c,
    isCustom: true,
  }));
  return [...extra, ...base];
}

export function subscribeTemplates(cb) {
  const fn = () => cb();
  window.addEventListener(EVT, fn);
  return () => window.removeEventListener(EVT, fn);
}

/** @param {string} key */
export function updateTemplateOverride(key, patch) {
  const cur = readTemplatesStore();
  const overrides = { ...cur.overrides, [key]: { ...(cur.overrides[key] || {}), ...patch } };
  writeTemplatesStore({ ...cur, overrides });
}

/** @param {Omit<CommsTemplateRow, 'id'|'isCustom'> & { id?: string }} row */
export function addCustomTemplate(row) {
  const cur = readTemplatesStore();
  const id = row.id || `ct_${Date.now()}`;
  const key = row.key && String(row.key).trim() ? String(row.key).trim() : `custom_${Date.now()}`;
  const customs = [
    {
      id,
      key,
      category: row.category || 'general',
      channel: row.channel || 'email',
      status: row.status || 'draft',
      updatedAt: new Date().toISOString().slice(0, 10),
      vars: row.vars || '{{employee_name}}',
      displayName: row.displayName || key,
      previewText: row.previewText || '',
      isCustom: true,
    },
    ...cur.customs,
  ];
  writeTemplatesStore({ ...cur, customs });
}

export function updateCustomTemplate(id, patch) {
  const cur = readTemplatesStore();
  const customs = cur.customs.map((c) => (c.id === id ? { ...c, ...patch, updatedAt: new Date().toISOString().slice(0, 10) } : c));
  writeTemplatesStore({ ...cur, customs });
}

export function removeCustomTemplate(id) {
  const cur = readTemplatesStore();
  writeTemplatesStore({ ...cur, customs: cur.customs.filter((c) => c.id !== id) });
}

/** Duplica una fila (demo o custom) en un nuevo custom */
export function duplicateTemplateRow(source) {
  const baseName = source.displayName || source.key;
  addCustomTemplate({
    key: `custom_${Date.now()}`,
    category: source.category || 'general',
    channel: source.channel || 'email',
    status: 'draft',
    vars: source.vars || '{{employee_name}}',
    displayName: `${baseName} (copia)`,
    previewText: source.previewText || '',
  });
}
